import React from 'react';
import { inject, observer } from 'mobx-react';
import { Link } from 'react-router';
import { Icon, Button, Header, Popup, Loader, Image } from 'semantic-ui-react';
import { graphql } from 'react-apollo';
import gql from 'graphql-tag';
import { cloneDeep, every } from 'lodash';

import Paginator from '../../components/Paginator';
import Table from '../../components/Table';
import Page from '../../components/Page';
import Modal from '../../components/Modal';
import BetaModal from '../../components/ui/Modal';
import BetaButton from '../../components/ui/Button';
import Avatar from '../../components/Avatar';
import RoleForm from '../Role/Form';
import EntityPatch from '../Entity/Patch';
import * as analytics from '../../lib/analytics';
import Cookies from 'js-cookie';

import { __ } from '../../i18n';
import * as utils from '../../utils';
import { simpleFiltersList } from './helpers/simpleFiltersHelpers';
import { getProfiles } from './helpers/getProfiles';
import { isNetwork } from '../../app/Domain/UseCases/Unleash/validations/IsNetwork';
import { TRACK_EVENT_PAGE_NAME } from '../../lib/mixpanel/pages';

const PAGE_SIZE = 40;

const updateEntity = (
  input,
) => `updateEntity_${input.id}: updateEntity(input: ${JSON.stringify(input).replace(/\"([^(\")"]+)\":/g, '$1:')}) {
    clientMutationId
}`;

const inviteEntity = (
  input,
) => `updateEntity_${input.id}: updateEntity(input: { id: ${input.id}, invite: ${input.invite} }) {
    clientMutationId
}`;

const deleteEntity = (
  input,
) => `deleteEntity_${input.id}: deleteEntity(input: { id: ${input.id} }) {
    clientMutationId
}`;

@inject('store', 'api', 'client')
@graphql(
  gql`
    query OrganizationEntitiesQuery(
      $id: ID!
      $limit: Int
      $offset: Int
      $search: String
      $entityType: [EntityType]
      $groups: [ID]
      $status: OrganizationEntitiesStatus
      $orderBy: [OrganizationEntitiesOrder]
      $noRole: Boolean
      $seeAll: Boolean
      $isChannel: Boolean
      $getAllEntities: Boolean
    ) {
      node(id: $id) @connection(key: "Organization", filter: ["id"]) {
        ... on Organization {
          id: dbId
          plan
          premiumPeriod
          isDemonstration
          monthlyMessagesCount
          monthlyMessageQuota
          entities(
            limit: $limit
            offset: $offset
            search: $search
            type: $entityType
            groups: $groups
            status: $status
            orderBy: $orderBy
            noRole: $noRole
            seeAll: $seeAll
            getAllEntities: $getAllEntities
            isChannel: $isChannel
          ) {
            totalCount
            nodes {
              id: dbId
              fullname
              eid
              picture {
                uri
                id: dbId
                key
              }
              type
              code
              description
              invisible
              isChannel
              disabled
              seeAll
              selected
              users {
                totalCount
              }
              invitedAddresses: addressesCount(invited: true) {
                totalCount
              }
              addresses: addressesCount {
                totalCount
              }
              roles(limit: 400) {
                nodes {
                  id: dbId
                  name
                }
              }
              groups(limit: 400) {
                nodes {
                  id: dbId
                  name
                }
                totalCount
              }
            }
          }
          groups(limit: 400) {
            nodes {
              id: dbId
              name
            }
            totalCount
          }
        }
      }
    }
  `,
  {
    options: (ownProps) => ({
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network',
      variables: {
        id:
          ownProps.params.organization_id ||
          ownProps.store.currentOrganization.id,
        limit: parseInt(ownProps.location.query.limit || PAGE_SIZE, 10),
        offset: parseInt(
          ((ownProps.location.query.p || 1) - 1) *
            (ownProps.location.query.limit || PAGE_SIZE),
          10,
        ),
        search: ownProps.location.query.search || '',
        getAllEntities: !!ownProps.location.query.search,
        entityType:
          (ownProps.location.query.entityType && [
            ownProps.location.query.entityType,
          ]) ||
          [],
        groups:
          (ownProps.location.query.groupsIds &&
            JSON.parse(ownProps.location.query.groupsIds)) ||
          [],
        status: ownProps.location.query.status || null,
        orderBy:
          (ownProps.location.query.orderBy &&
            JSON.parse(ownProps.location.query.orderBy)) ||
          (ownProps.location.query.search
            ? { column: 'DISABLED', direction: 'ASC' }
            : { column: 'CREATED', direction: 'DESC' }),
        noRole:
          ownProps.location.query.noGroup &&
          JSON.parse(ownProps.location.query.noGroup),
        seeAll:
          ownProps.location.query.seeAll &&
          JSON.parse(ownProps.location.query.seeAll),
        isChannel: false,
      },
    }),
  },
)
@observer
export default class OrganizationEntities extends Page {
  constructor(props) {
    super(props);
    this.colNames = this.isMobile()
      ? [__('Name'), '']
      : [
        __('Name'),
        __('Profile'),
        __('Groups'),
        __('Users'),
        __('Status'),
        '',
      ];

    const { location } = props;
    const query = { ...location.query };

    const status = [
      { text: __('Registered'), value: 'WITH_USERS' },
      { text: __('Not registered'), value: 'NO_USERS' },
      {
        text: __('Invited but not registered'),
        value: 'WITH_INVITES_NO_USERS',
      },
      { text: __('No addresses'), value: 'NO_ADDRESSES' },
      { text: __('Disabled'), value: 'DISABLED' },
    ];
    const order = [
      { text: __('Created'), value: '{"column":"CREATED","direction":"DESC"}' },
      {
        text: __('Name A-Z'),
        value: '{"column":"FULLNAME","direction":"ASC"}',
      },
      {
        text: __('Name Z-A'),
        value: '{"column":"FULLNAME","direction":"DESC"}',
      },
    ];
    const usersFromCookies = Cookies.get('newStudentFlowAlert');
    const hasUserSeenModal = this.getUserHasSeenModal(
      this.props.store.currentUser.id,
      usersFromCookies,
    );
    const hasFeatureFlag =
      this.props.store.currentOrganization?.unleashStatus?.new_student_flow;
    const shouldAppearModal =
      hasFeatureFlag &&
      this.props.store.currentEntity.type == 'ADMIN' &&
      !hasUserSeenModal;
    const profiles = getProfiles(props.store.currentOrganization) ?? [];

    this.state = {
      groups: [],
      profiles: profiles.map((e) => ({
        value: e.value,
        text: e.text,
        selected: query.entityType === e.value,
        notVisible: e.notVisible,
      })),
      status: status.map((e) => ({
        value: e.value,
        text: e.text,
        selected: query.status === e.value,
      })),
      order: order.map((e) => ({
        value: e.value,
        text: e.text,
        selected: query.orderBy === e.value,
      })),
      selectedGroups: [],
      selectAll: false,
      open: false,
      screenWidth: window.innerWidth,
      showModalNewFlowStudent: shouldAppearModal,
    };

    this.updateWindowWidth = this.updateWindowWidth.bind(this);
  }

  getUserHasSeenModal(userId, usersFromCookies) {
    if (!usersFromCookies) {
      return false;
    }
    return decodeURIComponent(usersFromCookies)
      .split(',')
      .some((id) => id === userId.toString());
  }

  renderModalNewFlowStudent() {
    const closeModalForever = () => {
      const usersFromCookies = Cookies.get('newStudentFlowAlert');
      const hasUserSeenModal = this.getUserHasSeenModal(
        this.props.store.currentUser.id,
        usersFromCookies,
      );
      let usersToSet = this.props.store.currentUser.id;
      if (usersFromCookies && !hasUserSeenModal) {
        usersToSet = `${usersFromCookies},${this.props.store.currentUser.id}`;
      }
      Cookies.set('newStudentFlowAlert', usersToSet);
      this.setState({ showModalNewFlowStudent: false });
    };

    return (
      <BetaModal
        onClose={closeModalForever}
        size="small"
        header={__('Even safer and more reliable data!')}
        content={
          <div
            data-testId="newFlowStudentAlert"
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
              textAlign: 'center',
            }}
          >
            <img
              src="/public/images/newStudentFlowBanner.png"
              style={{ width: '45%', margin: 'auto', marginBottom: '2em' }}
            />
            <p style={{ marginBottom: '1em' }}>
              {__(
                'Now, when registering or editing a student, you will find two new fields:',
              )}{' '}
              <b> {__('date of birth')} </b> {__('and')} <b> CPF. </b>{' '}
              {__(
                'These changes were designed to enhance security and ensure more complete and organized information in the system.',
              )}
            </p>
            <p>
              {__(
                'With this update, your experience will be even more practical and efficient, making student management easier and increasing confidence in your data.',
              )}
            </p>
            <p style={{ marginBottom: '2em' }}>
              <a
                target="_blank"
                href="https://ajuda.classapp.com.br/hc/pt-br/articles/30726003897755-Como-atualizar-os-dados-cadastrais-dos-alunos "
                rel="noreferrer"
              >
                {__('Click here to learn more')}
              </a>{' '}
              {__('about this update and how it can benefit you!')}
            </p>
            <Button
              data-testId="letsGoButton"
              data-action="submit"
              primary
              content={__("Let's Go")}
              style={{
                width: 'fit-content',
                paddingLeft: '60px',
                paddingRight: '60px',
                borderRadius: '100px',
              }}
              onClick={closeModalForever}
            />
          </div>
        }
      />
    );
  }

  componentDidMount() {
    this.updateWindowWidth();
    window.addEventListener('resize', this.updateWindowWidth);
    try {
      if (hj && this.props.store.currentOrganization.plan === 'FREE') {
        hj('trigger', 'pessoasfree');
      }
    } catch (e) {
      console.error(e);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateWindowWidth);
  }

  updateWindowWidth() {
    this.setState({ screenWidth: window.innerWidth });
  }

  async UNSAFE_componentWillReceiveProps(nextProps) {
    const { data, client, store } = nextProps;

    if ((data.loading && !data.node) || !data.node) return;

    const { totalCount, nodes } = data.node.groups;

    if (totalCount === nodes.length) return;

    try {
      const response = await client.query({
        query: gql`
          query OrganizationEntitiesGroupsQuery($id: ID!, $limit: Int) {
            node(id: $id) {
              ... on Organization {
                id: dbId
                groups(limit: $limit) {
                  nodes {
                    id: dbId
                    name
                  }
                }
              }
            }
          }
        `,
        variables: {
          id: store.currentOrganization.id,
          limit: totalCount,
        },
      });

      data.updateQuery((previousResult) => {
        const nextResult = cloneDeep(previousResult);
        nextResult.node.groups.nodes = response.data.node.groups.nodes;

        return nextResult;
      });
    } catch (e) {
      console.log(e);
    }
  }

  onEntitySelect = (entity_id) => {
    const { data } = this.props;

    data.updateQuery((previousResult) => {
      const newEntities = previousResult.node.entities.nodes.map((node) =>
        node.id !== entity_id ? node : { ...node, selected: !node.selected },
      );

      return Object.assign({}, previousResult, {
        node: {
          ...previousResult.node,
          entities: { ...previousResult.node.entities, nodes: newEntities },
        },
      });
    });

    this.setState({ selectAll: false });
  };

  onSelectAll = () => {
    const selectAll = !this.state.selectAll;
    const { data, store } = this.props;

    data.updateQuery((previousResult) => {
      const newEntities = previousResult.node.entities.nodes.map((node) => ({
        ...node,
        selected:
          store.currentEntity.type === 'ADMIN' || node.type !== 'ADMIN'
            ? selectAll
            : node.selected,
      }));

      return Object.assign({}, previousResult, {
        node: {
          ...previousResult.node,
          entities: { ...previousResult.node.entities, nodes: newEntities },
        },
      });
    });

    this.setState({ selectAll });
  };

  onSubmitError = (params) => {
    if (
      params &&
      params.values &&
      params.values.name &&
      params.values.name &&
      (params.values.name === 'disable' ||
        params.values.name === 'delete' ||
        params.values.name === 'invite')
    ) {
      this.props.store.snackbar = {
        active: true,
        message: __('Select at last one person'),
        success: false,
      };
    }
  };

  confirmModal = async (selectedEntities = [], name = 'disable') => {
    let title = '';
    let message = '';
    let submitButton = '';
    let red = false;

    let values = {
      entities: selectedEntities.map((e) => {
        if (name === 'transform' && e.type === 'STUDENT') return null;
        return e.id;
      }),
      name,
    };
    values = {
      entities: values.entities.filter((e) => e !== null),
      name: values.name,
    };

    if (name === 'disable') {
      title = __('Disable people');
      message = __('Are you sure you want to disable these people?');
      submitButton = __('Disable');
    } else if (name === 'delete') {
      title = __('Delete people');
      message = __('Are you sure you want to delete these people?');
      submitButton = __('Delete');
      red = true;
    } else if (name === 'enable') {
      title = __('Enable people');
      message = __('Are you sure you want to enable these people?');
      submitButton = __('Enable');
    } else if (name === 'invite') {
      title = __('Invite people');
      message = __('Are you sure you want to invite these people?');
      submitButton = __('Invite');
      values.invite = 'EMAIL';
    } else if (name === 'transform') {
      title = __('Transform people into channels');
      message = __(
        'Are you sure about that? When you do this, these people will be located in the Channel menu.',
      );
      submitButton = __('Transform');
    }
    this.props.store.appends.push(
      <BetaModal
        data-title="confirm-modal"
        id={'EntityPatch'}
        data-params={name}
        onClose={() => this.props.store.appends.pop()}
        header={title}
        content={
          <EntityPatch
            modal
            type={'entity'}
            message={message}
            values={values}
            entityTypes={name === 'transform' && ['ADMIN', 'STAFF']}
            entities={
              name === 'transform'
                ? this.props.data.node.entities.nodes.filter(
                  (e) => e.type !== 'STUDENT',
                )
                : this.props.data.node.entities.nodes
            }
            onSubmit={this.updateEntities}
            onSubmitError={this.onSubmitError}
            submitButton={submitButton}
            red={red}
            pageName={TRACK_EVENT_PAGE_NAME.ENTITY_LIST}
          />
        }
        actions={[
          <BetaButton
            data-action="cancel"
            floated="left"
            key={0}
            round
            transparent
            text={__('Cancel')}
            onClick={() => this.props.store.appends.pop()}
          />,
        ]}
      />,
    );
  };

  renderUpdateEntitiesError = (errors) =>
    this.props.store.appends.push(
      <Modal
        onClose={() => this.props.store.appends.pop()}
        size="tiny"
        actions={[
          <Button
            data-action="submit"
            primary
            content={__('OK')}
            onClick={() => this.props.store.appends.pop()}
          />,
        ]}
        header={__('Errors occurred updating entities')}
        content={
          <div>
            {errors.map((err) => {
              const entity = this.props.data.node.entities.nodes.find(
                (e) => e.id == err.id,
              );

              return (
                <Header
                  as="h5"
                  className="entityItem"
                  title={entity.fullname}
                  data-id={entity.id}
                  style={{ display: 'flex', alignItems: 'flex-start' }}
                >
                  <Avatar
                    avatar
                    spaced="right"
                    src={entity.picture && entity.picture.uri}
                    alt={entity.fullname || ''}
                  />
                  <Header.Content>
                    {entity.fullname}
                    <Header.Subheader>
                      {utils.handleError(err)}
                    </Header.Subheader>
                  </Header.Content>
                </Header>
              );
            })}
          </div>
        }
      />,
    );

  renderInfosModal = () =>
    this.props.store.appends.push(
      <BetaModal
        id="InfoModal"
        onClose={() => this.props.store.appends.pop()}
        closeOnBack
        size="large"
        style={{ width: 903, marginLeft: -451 }}
        header={
          <span>
            {__('Learn to Register Users')}
            <Icon name="users" style={{ fontWeight: 300, marginLeft: 12 }} />
          </span>
        }
        content={
          <div>
            <div style={{ marginBottom: 20 }}>
              <p>
                {__(
                  'Watch the video and discover everything you can do in the People menu.',
                )}
              </p>
              <p>
                {__(
                  'If you prefer, open it on Youtube to continue watching it while using it. ',
                )}
                <a
                  href="https://youtu.be/3aduxzPsnA0"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#094fff' }}
                >
                  {__('Youtube')}
                  <Icon name="square arrow up right" />
                </a>
              </p>
            </div>
            <iframe
              style={{ borderRadius: 12 }}
              width="855"
              height="481"
              src="https://www.youtube.com/embed/3aduxzPsnA0"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope"
              allowFullScreen
            />
          </div>
        }
      />,
    );

  updateEntities = async (values) => {
    const { entities, name, invite } = values;
    let data = [];
    let message = '';

    if (name === 'delete') {
      data = entities.map((id) => ({ id }));
      message =
        entities.length > 1
          ? __('People were deleted successfully')
          : __('Person was deleted successfully');
    } else if (name === 'disable') {
      data = entities.map((id) => ({ id, disabled: true }));
      message =
        entities.length > 1
          ? __('People were disabled successfully')
          : __('Person was disabled successfully');
    } else if (name === 'enable') {
      data = entities.map((id) => ({ id, disabled: false }));
      message =
        entities.length > 1
          ? __('People were enabled successfully')
          : __('Person was enabled successfully');
    } else if (name === 'invite') {
      data = entities.map((id) => ({ id, invite }));
      message =
        entities.length > 1
          ? __('People were invited successfully')
          : __('Person was invited successfully');
    } else if (name === 'roles') {
      data = values.entities.map((entity_id) => ({
        id: entity_id,
        groups: values.groups,
        attach: values.attach ? ['groups'] : [],
      }));
      message = __('Roles were changed successfully');
    } else if (name === 'transform') {
      data = values.entities.map((entity_id) => ({
        id: entity_id,
        invisible: false,
      }));
      message = __('People were transformed successfully');
    }

    if (name === 'delete') {
      return this.props.api
        .patch('mutation', deleteEntity, data)
        .then(async (data) => {
          await this.props.client.resetStore();
          this.props.store.appends.pop();
          this.props.store.snackbar = { active: true, message, success: true };
        })
        .catch((e) => console.error(e));
    }

    return this.props.api
      .patch('mutation', name === 'invite' ? inviteEntity : updateEntity, data)
      .then(async (data) => {
        this.setState({ selectAll: false });

        await this.props.client.resetStore();

        this.props.store.appends.pop();

        const errors = data.reduce(
          (prev, item) =>
            prev.concat(
              item.errors
                ? item.errors.map((e) => ({
                  id: e.path[0].replace('updateEntity_', ''),
                  message: e.message,
                }))
                : [],
            ),
          [],
        );

        if (errors.length) {
          this.renderUpdateEntitiesError(errors);
        } else {
          this.props.store.snackbar = { active: true, message, success: true };
        }
      });
  };

  openRoleForm = async (selectedEntities = [], entities) => {
    this.props.store.appends.push(
      <BetaModal
        id={'RoleAdd'}
        onClose={() => this.props.store.appends.pop()}
        size="small"
        actions={[
          <BetaButton
            data-action="cancel"
            floated="left"
            round
            transparent
            text={__('Cancel')}
            onClick={() => this.props.store.appends.pop()}
          />,
        ]}
        header={__('Add people to groups')}
        content={
          <RoleForm
            modal
            type="entity"
            multipleGroups
            values={{ entities: selectedEntities, name: 'roles' }}
            onSubmit={this.updateEntities}
            submitButton={__('Add')}
            entities={entities}
          />
        }
      />,
    );
  };

  onAddFilter = async (value, key) => {
    this.setState({ loading: true });
    this.setState(
      (prevState) => {
        if (value === 'clear-all') {
          prevState[key] = prevState[key].map((x) => ({
            ...x,
            selected: false,
          }));
          return prevState;
        }
        const index = prevState[key].findIndex((x) => x.value === value);
        prevState[key][index].selected = true;
        return prevState;
      },
      async () => {
        const allIds = this.state[key]
          .map((i) => i.selected && i.value)
          .filter((i) => i);
        const ids = this.state[key]
          .map((i) => i.selected && i.value)
          .filter((i) => i && i !== 'noGroup' && i !== 'seeAll');
        if (key === 'groups') {
          const indexNoGroup = allIds.indexOf('noGroup');
          if (indexNoGroup > -1) {
            await this.onUniqueParameterChange('noGroup', 'true');
          } else {
            await this.onUniqueParameterChange('noGroup', 'false');
          }
          const indexSeeAll = allIds.indexOf('seeAll');
          if (indexSeeAll > -1) {
            await this.onUniqueParameterChange('seeAll', 'true');
          } else {
            await this.onUniqueParameterChange('seeAll', 'false');
          }
          this.onMultipleParameterChange('groupsIds', ids);
        }
      },
    );
    this.setState({ loading: false });
  };

  onDeleteFilter = (filter) => {
    this.setState({ loading: true });
    const value = filter.value;
    const key = filter.key;
    if (filter.multipleTags) {
      this.onDelete(value, key);
      return null;
    }

    this.setState(
      (prevState) => {
        const index = prevState[key].findIndex((x) => x.value === value);
        prevState[key][index].selected = false;
        return prevState;
      },
      async () => {
        const allIds = this.state[key]
          .map((i) => i.selected && i.value)
          .filter((i) => i);
        const ids = this.state[key]
          .map((i) => i.selected && i.value)
          .filter((i) => i && i !== 'noGroup' && i !== 'seeAll');
        if (key === 'groups') {
          const indexNoGroup = allIds.indexOf('noGroup');
          if (indexNoGroup > -1) {
            await this.onUniqueParameterChange('noGroup', 'true');
          } else {
            await this.onUniqueParameterChange('noGroup', 'false');
          }
          const indexSeeAll = allIds.indexOf('seeAll');
          if (indexSeeAll > -1) {
            await this.onUniqueParameterChange('seeAll', 'true');
          } else {
            await this.onUniqueParameterChange('seeAll', 'false');
          }
          this.onMultipleParameterChange('groupsIds', ids);
        }
      },
    );
    this.setState({ loading: false });
  };

  /**
   * @params (required) object with informations about filter {
   *     filterBy: string, that indicate the filter item selected
   *     key: string, that indicate what filter is
   *   }
   **/
  simpleFiltersCallback = (params) => {
    const keys = {
      profiles: 'entityType',
      status: 'status',
      order: 'orderBy',
      groups: 'groups',
    };
    let value = params.filterBy;
    const key = params.key;
    if (params.multipleTags) {
      this.onAddFilter(value, key);
      return null;
    }

    this.setState({ loading: true });
    if (value === 'clear-all') {
      this.onUniqueParameterChange(keys[key], '');
      this.setState((prevState) => {
        prevState[key] = prevState[key].map((x) => ({ ...x, selected: false }));
        return prevState;
      });
    } else {
      this.onUniqueParameterChange(keys[key], value);
      this.setState((prevState) => {
        if (key != 'order') {
          const index = prevState[key].findIndex((x) => x.value === value);
          prevState[key] = prevState[key].map((x) => ({
            ...x,
            selected: false,
          }));
          prevState[key][index].selected = true;
        }
        return prevState;
      });
    }

    this.setState({ loading: false });
  };

  onChangeText = (value) => {
    clearTimeout(this.lastRequestId);
    let id;
    this.lastRequestId = id = setTimeout(
      () => this.onUniqueParameterChange('search', value),
      600,
    );
  };

  sendAnalytics = (category, label, store) => {
    analytics.sendGoogleAnalyticsEvent(
      {
        name: 'People',
        category: `${category}`,
        label,
      },
      { store },
    );
  };

  menuDropDownCallback = (value) => {
    const { router, store, location } = this.props;
    const query = { ...location.query };
    const lang = store.app.locale
      ? store.app.locale === 'pt-BR'
        ? 'pt'
        : store.app.locale
      : 'en';

    if (query.groupsIds) {
      query.groupsIds = JSON.parse(query.groupsIds);
    }

    if (query.noGroup) {
      query.noRole = query.noGroup;
      delete query.noGroup;
    }

    const exportLink =
      `${store.app.url}/csv/organization/entities?` +
      `organization_id=${store.currentOrganization.id}&${query.limit ? '' : 'limit=40000'}&tz_offset=${-new Date().getTimezoneOffset()}&locale=${lang}&${utils.stringify(query)}`;
    switch (value) {
      case 'import-student':
        router.push(
        `/organizations/${store.currentOrganization.id}/entities/import/student`,
      );
        this.sendAnalytics(
        'import_student',
        `OrganizationId: ${store.currentOrganization.id}`,
        store,
      );
        break;
      case 'import-staff':
        router.push(
        `/organizations/${store.currentOrganization.id}/entities/import/staff`,
      );
        this.sendAnalytics(
        'import_staff',
        `OrganizationId: ${store.currentOrganization.id}`,
        store,
      );
        break;
      case 'send-invites':
        router.push(
        `/organizations/${store.currentOrganization.id}/invitations`,
      );
        break;
      case 'email-invite':
        router.push(
        `/organizations/${store.currentOrganization.id}/invitations?type=EMAIL`,
      );
        break;
      case 'phone-invite':
        router.push(
        `/organizations/${store.currentOrganization.id}/invitations?type=PHONE`,
      );
        break;
      case 'export':
        this.props.router.push({
          pathname: `/organizations/${store.currentOrganization.id}/entities/export`,
          search: location.search,
          state: {
            exportLink: exportLink,
            accessToken: store.access_token,
          },
        });
        break;
      default:
        break;
    }
  };

  addPerson = () => {
    const { router, store } = this.props;
    router.push(`/organizations/${store.currentOrganization.id}/entities/add`);
    this.sendAnalytics(
      'add_people',
      `OrganizationId: ${store.currentOrganization.id}`,
      store,
    );
  };

  renderTable = ({ rows, loading, data, pagination }) => {
    const { store, location } = this.props;
    const selectedEntities = data.filter((e) => e.selected);
    const organizationType = store.currentOrganization.type;
    const isAllSelectedEntitiesDisabled = every(selectedEntities, [
      'disabled',
      true,
    ]);
    const isFreeOrganization =
      store.currentOrganization && store.currentOrganization.plan === 'FREE';

    const hasPermission =
      (store.currentEntity && store.currentEntity.type === 'ADMIN') ||
      (store.currentEntity &&
        store.currentEntity.organization &&
        store.currentEntity.organization.permissions &&
        (store.currentEntity.organization.permissions.hasAdmin ||
          store.currentEntity.organization.permissions.entityScopes.includes(
            'CREATE_UPDATE_ENTITY',
          )));

    let listFilters = simpleFiltersList(store.currentOrganization);

    const simpleFilters = listFilters.map(
      ({
        key,
        name,
        removeButton,
        multipleTags,
        searchable,
        style,
        icon,
        notVisible,
      }) => ({
        removeButton,
        multipleTags,
        searchable,
        notVisible,
        style,
        icon,
        key,
        placeholder: name,
        options: this.state[key],
        fn: (value) => this.simpleFiltersCallback(value),
        onDelete: this.onDeleteFilter,
      }),
    );

    const menuDropDown = {
      name: __('More actions'),
      icon: 'chevron down',
      options: hasPermission
        ? [
          {
            text:
                utils.checkIfItIsClientOrSchool(organizationType) === 1
                  ? __('Import/update students')
                  : __('Import/update clients'),
            value: 'import-student',
          },
          { text: __('Import/update staff'), value: 'import-staff' },
        ]
        : [],
      fn: (value) => this.menuDropDownCallback(value),
    };

    if (store.currentUser.isMaster) {
      menuDropDown.options = [
        ...menuDropDown.options,
        { text: __('Send invites'), value: 'send-invites' },
        { text: __('Invite by e-mail'), value: 'email-invite' },
        { text: __('Invite by phone'), value: 'phone-invite' },
      ];
    }
    menuDropDown.options.push({ text: __('Export'), value: 'export' });

    let menuButton;
    if (this.isMobile()) {
      menuButton = { icon: 'plus', fn: () => this.addPerson() };
    }

    const organizationIsNetwork = isNetwork(store.currentOrganization);
    const shouldShowMenuDropdown =
      !organizationIsNetwork ||
      (organizationIsNetwork && store.currentUser.isMaster);

    const menu =
      this.isMobile() || !shouldShowMenuDropdown
        ? []
        : [{ type: 'dropdown', params: menuDropDown }];

    if (hasPermission && menuButton) {
      menu.push({ type: 'button', params: menuButton });
    }
    let selectedMenu = [];
    if (hasPermission) {
      selectedMenu.push({
        name: isAllSelectedEntitiesDisabled ? __('Enable') : __('Disable'),
        icon: isAllSelectedEntitiesDisabled ? 'user' : 'user slash',
        fn: () =>
          this.confirmModal(
            selectedEntities,
            isAllSelectedEntitiesDisabled ? 'enable' : 'disable',
          ),
      });
      selectedMenu.push({
        name: __('Invite people'),
        icon: 'user plus',
        fn: () => this.confirmModal(selectedEntities, 'invite'),
      });
      selectedMenu.push({
        name: __('Add people to groups'),
        icon: 'folder user',
        fn: () => {
          this.openRoleForm(
            selectedEntities.map((e) => e.id),
            data,
          );
          this.sendAnalytics(
            'add_people_to_group',
            'Add people to groups',
            store,
          );
        },
      });
      if (
        selectedEntities.filter((e) => e.type === 'ADMIN' || e.type === 'STAFF')
          .length > 0
      ) {
        selectedMenu.push({
          name: __('Tranform into channels'),
          icon: 'exchange alt',
          fn: () => this.confirmModal(selectedEntities, 'transform'),
        });
      }
    }

    if (store.currentUser.isMaster) {
      selectedMenu.push({
        name: __('Delete'),
        icon: 'trash',
        fn: () => this.confirmModal(selectedEntities, 'delete'),
      });
    }

    const { entityType, groupsIds, status, search, seeAll, noGroup } =
      this.props.location.query;
    const renderChannelWarn =
      ((!!groupsIds ||
        !!status ||
        !!search ||
        seeAll === 'true' ||
        noGroup === 'true') &&
        entityType !== 'STUDENT' &&
        entityType !== 'CLIENT') ||
      entityType === 'STAFF' ||
      entityType === 'ADMIN';

    return (
      <div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 40,
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
            }}
          >
            <h2
              style={{
                paddingLeft: 8,
                fontSize: '2.29rem',
                lineHeight: '38px',
                paddingBottom: '3px',
              }}
            >
              {__('Entities')}
            </h2>
            {!this.isMobile() && isFreeOrganization && (
              <div
                style={{
                  cursor: 'pointer',
                  marginLeft: '11px',
                  display: 'flex',
                }}
                onClick={() => this.renderInfosModal()}
              >
                <Icon
                  style={{
                    fontWeight: 300,
                    color: 'rgba(0, 0, 0, 0.24)',
                    fontSize: '20px',
                  }}
                  name="play circle"
                />
                <span style={{ fontSize: '16px', color: 'rgba(0, 0, 0, 0.4)' }}>
                  {__('See how to use')}
                </span>
              </div>
            )}
          </div>
          {hasPermission && !this.isMobile() && (
            <div
              onClick={() => this.addPerson()}
              style={{ display: 'flex', alignContent: 'center' }}
            >
              <div
                className="generic-button"
                style={{ backgroundColor: '#084FFF' }}
              >
                <div className="wrapper">
                  <Icon className="plus" style={{ margin: 0, color: '#FFF' }} />
                  <span
                    style={{ marginLeft: '5px', color: '#FFF', fontSize: 16 }}
                  >
                    {__('Add person')}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
        <Table
          colNames={[...this.colNames, '']}
          hideNotFoundImage={renderChannelWarn}
          selectColumn
          selectedCount={!loading && selectedEntities.length}
          withSelection={!this.isMobile()}
          onSelectAll={() => this.onSelectAll()}
          selectAll={this.state.selectAll}
          onEntitySelect={(entity_id) => this.onEntitySelect(entity_id)}
          data={data}
          rows={rows}
          search={{
            placeholder: __('Search...'),
            api: { fn: (value) => this.onChangeText(value) },
            fillAllWidth: this.isMobile(),
          }}
          simpleFilters={this.isMobile() ? null : simpleFilters}
          loading={loading}
          gridColumns={
            this.isMobile() ? '8fr 0.5fr' : '0.5fr 8fr 4fr 4fr 4fr 4fr 0.5fr'
          }
          menuDropDown={menuDropDown}
          menu={menu}
          selectedMenu={selectedMenu}
          location={location}
          type={'entity'}
        />
        {renderChannelWarn && !loading && this.renderChannelWarn({ rows })}
        <Paginator
          isMobile={this.isMobile}
          source={data}
          pagination={pagination}
          onLoadMore={this.eventReload}
        />
      </div>
    );
  };

  renderChannelWarn = ({ rows }) => (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        marginTop: rows.length > 0 ? '30px' : '0px',
      }}
    >
      <div
        style={{
          maxWidth: '238px',
          flexDirection: 'row',
          textAlign: 'center',
          color: 'rgba(0, 0, 0, 0.8)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '20px',
          }}
        >
          <Image
            src={utils.asset('/images/light-on.png')}
            style={{ maxHeight: '79px' }}
          />
        </div>
        <div>{__('Did not find what you wanted?')}</div>
        <div>{__('Also search in Channels')}</div>
      </div>
    </div>
  );

  renderEntityFullname = ({ to, fullname, style, disabledIcon }) => {
    const isAdmin =
      this.props.store.currentEntity &&
      this.props.store.currentEntity.type === 'ADMIN';

    if (isAdmin)
      return (
        <Link to={to} style={{ color: '#084FFF' }}>
          {fullname}
          {disabledIcon && (
            <Icon
              style={{ color: 'rgba(0, 0, 0, 0.6)', marginLeft: '0.5rem' }}
              name="user slash"
            />
          )}
        </Link>
      );

    return <span style={style}>{fullname}</span>;
  };

  renderFullname = (entity) => {
    return (
      <div style={{ display: 'flex', height: '50px' }}>
        <div
          style={{
            display: 'flex',
            width: '50px',
            height: '100%',
            alignItems: 'center',
          }}
        >
          <Avatar
            style={{ width: '40px', height: '40px' }}
            avatar
            src={entity.picture && entity.picture.uri}
            alt={entity.fullname || ''}
            verticalAlign="middle"
          />
        </div>

        <div
          style={{
            paddingLeft: '1rem',
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            height: '100%',
            justifyContent: 'center',
          }}
        >
          {entity.fullname.length > 18 ? (
            <Popup
              trigger={this.renderEntityFullname({
                to: `/entities/${entity.id}`,
                fullname:
                  entity.fullname
                    .substring(0, !entity.disabled ? 18 : 14)
                    .trim() + '...',
                style: {
                  display: 'inline-block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
                disabledIcon: entity.disabled,
              })}
              content={entity.fullname}
            />
          ) : (
            this.renderEntityFullname({
              to: `/entities/${entity.id}`,
              fullname: entity.fullname,
              disabledIcon: entity.disabled,
            })
          )}

          {entity.description &&
            (entity.description.length > 20 ? (
              <Popup
                trigger={
                  <span
                    style={{
                      fontSize: '14px',
                      display: 'inline-block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      color: 'rgba(0, 0, 0, 0.6)',
                    }}
                  >
                    {entity.description.substring(0, 20).trim() + '...'}
                  </span>
                }
                content={<span>{entity.description}</span>}
              />
            ) : (
              <span style={{ fontSize: '14px', color: 'rgba(0, 0, 0, 0.6)' }}>
                {entity.description}
              </span>
            ))}
        </div>
      </div>
    );
  };

  renderEditButton = (entity) => {
    const { router, store } = this.props;
    const onClick = () => {
      router.push(
        `/organizations/${store.currentOrganization.id}/entities/${entity.id}/edit`,
      );
      this.sendAnalytics(
        'edit_people',
        `OrganizationId: ${store.currentOrganization.id}`,
        store,
      );
    };
    return !(
      (store.currentEntity && store.currentEntity.type === 'ADMIN') ||
      (store.currentEntity &&
        store.currentEntity.organization &&
        store.currentEntity.organization.permissions &&
        (store.currentEntity.organization.permissions.hasAdmin ||
          (store.currentEntity.organization.permissions.entityScopes.includes(
            'CREATE_UPDATE_ENTITY',
          ) &&
            entity.type !== 'ADMIN')))
    ) ? null : (
        <div
          id={`edit_people_${store.currentOrganization.id}_${entity.id}`}
          style={{ display: 'flex', alignContent: 'center', cursor: 'pointer' }}
          onClick={onClick}
        >
          <Icon name="pencil" style={{ fontWeight: 300 }} />
        </div>
      );
  };

  renderGroups = (entity) => {
    const { roles } = entity;
    let allGroups;
    if (entity.seeAll) {
      allGroups = [{ name: __('All') }, ...roles.nodes];
    } else {
      allGroups = [...roles.nodes];
    }
    if (allGroups.length > 0) {
      return (
        <Popup
          trigger={
            <div>{`${allGroups[0].name.length > 16 ? allGroups[0].name.substring(0, 16) + '...' : allGroups[0].name}${allGroups.length > 1 ? ` +${allGroups.length - 1}` : ''}`}</div>
          }
          content={
            <span>
              {allGroups.map((g, i) =>
                i == allGroups.length - 1 ? `${g.name}` : `${g.name}, `,
              )}
            </span>
          }
        />
      );
    }
    return '-';
  };

  renderType = (type) => {
    const organizationType = this.props.store.currentOrganization.type;
    const types = {
      STUDENT:
        utils.checkIfItIsClientOrSchool(organizationType) === 1
          ? __('Student')
          : __('Client'),
      STAFF: __('Staff'),
      ADMIN: __('Admin'),
    };
    return types[type];
  };

  renderStatus = (entity) => {
    if (entity.disabled) {
      return __('Disabled');
    }
    if (entity.users.totalCount > 0) {
      return __('Registered');
    }
    if (
      entity.invitedAddresses.totalCount > 0 &&
      entity.users.totalCount == 0
    ) {
      __('Invited');
    }
    if (entity.users.totalCount == 0) {
      return __('Not registered');
    }
    if (entity.addresses.totalCount == 0) {
      return __('No addresses');
    }
  };

  setGroupsFilters = () => {
    const { location } = this.props;
    const query = { ...location.query };
    let groupsIds = [];
    if (query.groupsIds) {
      groupsIds = JSON.parse(query.groupsIds);
    }
    let groups = [
      {
        value: 'noGroup',
        text: __('No group'),
        selected: query.noGroup ? JSON.parse(query.noGroup) : false,
      },
      {
        value: 'seeAll',
        text: __('All groups'),
        selected: query.seeAll ? JSON.parse(query.seeAll) : false,
      },
    ];
    groups = [
      ...groups,
      ...this.props.data.node.groups.nodes.map((g) => ({
        value: g.id,
        text: g.name,
        selected: groupsIds.includes(g.id),
      })),
    ];
    this.setState({ groups });
  };

  renderUsers = (entity) => {
    const { store } = this.props;
    const isMaster = store.currentUser.isMaster;
    if (utils.hasEntityInCurrentOrganization(store, ['ADMIN']) || isMaster) {
      return (
        <Link
          className={'hoverLink'}
          to={`/entities/${entity.id}/accounts`}
          onClick={() =>
            this.sendAnalytics('go_to_users', `EntityId: ${entity.id}`, store)
          }
          style={{
            color: '#000000',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '40px',
            width: '40px',
            borderRadius: '100px',
          }}
        >
          {entity.users.totalCount}
        </Link>
      );
    }
    return (
      <div
        style={{
          color: '#000000',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '40px',
          width: '40px',
        }}
      >
        {entity.users.totalCount}
      </div>
    );
  };

  render() {
    const { data, location } = this.props;
    if ((data.loading && !data.node) || !data.node)
      return <Loader active inline="centered" />;
    const entities = data.node.entities;
    const { nodes, totalCount } = entities;

    if (
      this.props.data.node.groups &&
      this.props.data.node.groups.nodes &&
      this.props.data.node.groups.nodes.length > 0 &&
      this.state.groups.length === 0
    ) {
      this.setGroupsFilters();
    }

    let preparedEntities;
    if (this.isMobile()) {
      preparedEntities = nodes.map((e) => [
        { value: this.renderFullname(e) },
        { value: this.renderEditButton(e) },
      ]);
    } else {
      preparedEntities = nodes.map((e) => [
        { value: this.renderFullname(e) },
        { value: this.renderType(e.type) },
        { value: this.renderGroups(e) },
        { value: this.renderUsers(e) },
        { value: this.renderStatus(e) },
        { value: this.renderEditButton(e) },
      ]);
    }

    return (
      <div id="OrganizationEntities">
        <div style={{ display: 'flex' }}>
          <div style={{ width: '100%' }}>
            <div>
              {this.renderTable({
                rows: preparedEntities,
                loading: data.loading,
                data: nodes,
                pagination: {
                  loading: false,
                  totalCount,
                  query: location.query,
                },
              })}
              {this.props.children}
            </div>
          </div>
        </div>
        {this.state.showModalNewFlowStudent && this.renderModalNewFlowStudent()}
      </div>
    );
  }
}
